/**
 * ExecutionProgress Component
 *
 * Displays real-time execution progress with SSE-driven updates
 * Shows phase status, connection indicator, and PR link on completion
 */

import React, { useEffect } from 'react';
import { useForgeSSE } from '../hooks/useForgeSSE';
import type { ExecutionProgressProps, PhaseStatus } from '../types/forge';
import styles from '../styles/forge.module.css';

const PHASE_LABELS = {
  analyzing: 'Analyzing Requirements',
  planning: 'Planning Implementation',
  implementing: 'Implementing Changes',
  testing: 'Running Tests',
  creating_pr: 'Creating Pull Request',
};

const PHASE_ICONS: Record<PhaseStatus, string> = {
  pending: '○',
  in_progress: '◉',
  completed: '✓',
  error: '✗',
};

export const ExecutionProgress: React.FC<ExecutionProgressProps> = ({
  requirementId,
  onComplete,
  onError,
}) => {
  const { isConnected, executionStatus, connect, error } = useForgeSSE();

  // Connect to SSE stream on mount
  useEffect(() => {
    connect(requirementId);
  }, [requirementId, connect]);

  // Handle execution completion
  useEffect(() => {
    if (executionStatus?.status === 'completed' && executionStatus.prUrl && onComplete) {
      onComplete(executionStatus.prUrl);
    }
  }, [executionStatus, onComplete]);

  // Handle execution error
  useEffect(() => {
    if (executionStatus?.status === 'failed' && executionStatus.error && onError) {
      onError(executionStatus.error);
    }
  }, [executionStatus, onError]);

  if (!executionStatus) {
    return (
      <div className={styles.executionProgress}>
        <div className={styles.executionProgressHeader}>
          <h3>Initializing Execution...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.executionProgress}>
      <div className={styles.executionProgressHeader}>
        <h3>Execution Progress</h3>
        <div className={styles.connectionStatus}>
          <span className={isConnected ? styles.connectionStatusLive : styles.connectionStatusReconnecting}>
            {isConnected ? '● Live' : '○ Reconnecting...'}
          </span>
        </div>
      </div>

      <div className={styles.executionPhaseList}>
        {executionStatus.phases.map((phase) => (
          <div
            key={phase.phase}
            className={`${styles.executionPhaseItem} ${
              styles[`executionPhaseItem--${phase.status}`]
            }`}
          >
            <span className={styles.executionPhaseIcon}>
              {PHASE_ICONS[phase.status]}
            </span>
            <span className={styles.executionPhaseLabel}>
              {PHASE_LABELS[phase.phase]}
            </span>
            {phase.error && (
              <span className={styles.executionPhaseError}>{phase.error}</span>
            )}
          </div>
        ))}
      </div>

      {executionStatus.status === 'completed' && executionStatus.prUrl && (
        <div className={styles.executionComplete}>
          <p>Execution completed successfully!</p>
          <a
            href={executionStatus.prUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.executionPrLink}
          >
            View Pull Request →
          </a>
        </div>
      )}

      {executionStatus.status === 'failed' && executionStatus.error && (
        <div className={styles.executionError}>
          <p>Execution failed:</p>
          <pre>{executionStatus.error}</pre>
        </div>
      )}

      {error && (
        <div className={styles.executionConnectionError}>
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default ExecutionProgress;
